/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Store
import useStore from '@store/index';

// Types
import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';
import {
	TApiResponseCreateApiToken,
	TBodyCreateApiToken,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addApiToken = (
	body: TBodyCreateApiToken
): Promise<TApiResponseCreateApiToken> => {
	return apiClient.post('/api_tokens', body);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddApiToken = (
	page: number,
	size: number
) => {
	const { setApiTokensLastAccessTokenCreated } =
		useStore();

	const { t } = useTranslation();

	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addApiToken,
		onSuccess: async (response) => {
			if (response) {
				const { value } = response;
				setApiTokensLastAccessTokenCreated(value);
			}

			notifications.show({
				title: t('global.success'),
				message: t(
					'apiTokens.notifications.apiTokenSuccessfullyAdded'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.apiTokens.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
